/**
 * El carro guardado en el navegador (CHK-02).
 *
 * Se guarda en `localStorage` y no en el servidor porque no hay cuentas de
 * cliente en esta version: el carro es de este navegador, no de una persona.
 *
 * Todo lo que sale de aca es sospechoso. Lo que se lee puede venir de una
 * version anterior del sitio, de otra pestana o de alguien que edito el
 * almacenamiento a mano, asi que se valida linea por linea y lo que no calza se
 * descarta en silencio. Un carro corrupto vacia el carro; nunca revienta la
 * tienda.
 *
 * Es el mismo criterio del token en `cliente-api.ts`: sin almacenamiento —
 * navegacion privada, permisos bloqueados— se trabaja sin recordar, que es
 * preferible a caerse.
 */
import type { Carro, ItemCarro, ProductoResumen } from '@bandu/compartido';

import { CARRO_VACIO } from './carro';

const CLAVE_CARRO = 'bandu:carro';

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null;
}

/**
 * Comprueba lo que la pantalla del carro necesita para dibujar la linea. El
 * resto de los campos del resumen los repone la conciliacion contra el catalogo.
 */
function esProducto(valor: unknown): valor is ProductoResumen {
  return (
    esObjeto(valor) &&
    typeof valor['sku'] === 'string' &&
    typeof valor['slug'] === 'string' &&
    typeof valor['nombre'] === 'string' &&
    typeof valor['precio'] === 'number'
  );
}

function esItem(valor: unknown): valor is ItemCarro {
  return (
    esObjeto(valor) &&
    esProducto(valor['producto']) &&
    typeof valor['cantidad'] === 'number' &&
    valor['cantidad'] > 0
  );
}

export function leerCarroGuardado(): Carro {
  let crudo: string | null = null;

  try {
    crudo = window.localStorage.getItem(CLAVE_CARRO);
  } catch {
    return CARRO_VACIO;
  }

  if (crudo === null) return CARRO_VACIO;

  try {
    const items: unknown = JSON.parse(crudo);
    if (!Array.isArray(items)) return CARRO_VACIO;

    const validos = items.filter(esItem);
    return {
      items: validos,
      subtotal: validos.reduce((suma, item) => suma + item.producto.precio * item.cantidad, 0),
    };
  } catch {
    // JSON roto: se descarta entero en vez de arrastrar medio carro.
    return CARRO_VACIO;
  }
}

/**
 * Guarda solo las lineas: el subtotal se recalcula al leer, para que no exista
 * la posibilidad de un total guardado que no cuadre con sus lineas.
 */
export function guardarCarro(carro: Carro): void {
  try {
    if (carro.items.length === 0) window.localStorage.removeItem(CLAVE_CARRO);
    else window.localStorage.setItem(CLAVE_CARRO, JSON.stringify(carro.items));
  } catch {
    // Sin almacenamiento el carro dura lo que dure la pestana. No es un error.
  }
}
